import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSplash } from '../../../contexts/SplashContext';
import CommonInput from '../../Common/CommonInput';
import CommonDate from '../../Common/CommonDate';
import CommonSelect from '../../Common/CommonSelect';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import { fetchSettings } from '../../../store/modules/saleinvoice/saleinvoiceActions';
import useFormSettings from '../../../hooks/useFormSettings';

import { 
    calculateGrossTotal, 
    calculateOverallDiscount,
    distributeHeaderDiscount,
    calculateTotals
} from './SaleInvoiceCalculation';

import customerService from '../../../services/customerService';
import deliveryNoteService from '../../../services/deliverynoteService';
import jobOrderLpoService from '../../../services/joborderlpoService';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import Deliverynotes from '../../../pages/Deliverynotes/Deliverynotes';
import { useHasPermission } from '../../../utils/permissionUtils';
import { updateSaleInvoiceCustomerLpo } from '../../../store/modules/saleinvoice/saleinvoiceActions';

const tagItemsWithDeliveryNote = (items, deliveryNoteId) =>
    (items || []).map((item) => ({ ...item, delivery_note_id: deliveryNoteId }));

const mergeItemsForDeliveryNote = (prevItems, newItems, deliveryNoteId, isRemoving) => {
    const withoutNote = (prevItems || []).filter((item) => item.delivery_note_id !== deliveryNoteId);
    if (isRemoving) {
        return withoutNote;
    }
    return [...withoutNote, ...tagItemsWithDeliveryNote(newItems, deliveryNoteId)];
};

const SaleInvoiceForm = ({ initialData, onSave, isLoading, onClose }) => {

    const hasCreatePermission = useHasPermission(['create sale invoices']);
    const hasUpdatePermission = useHasPermission(['update sale invoices']);
    const dispatch = useDispatch();

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('saleinvoice', fetchSettings);
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
    const [typingTimer, setTypingTimer]         = useState(null);
    const doneTypingInterval = 800;

    const [formData, setFormData] = useState({
        is_locked: false,
        id: null,
        selected_delivery_notes: [],
        invoice_number: '',
        invoice_date: '',
        lpo_number: '',
        lpo_date: '',
        customer_lpo: '',
        customer: null,
        customer_name: '',
        address1: '',
        address2: '',
        phone_number: '',
        invoice_description: {},
        gross_total: '',
        discount_percentage: '',
        discount_amount: '',
        sub_total: '',
        vat_amount: '',
        net_total: '',
        delivery_notes: [],
        items: [],
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingLpo, setIsSavingLpo] = useState(false);
    const [isLoadingCustomerOrders, setIsLoadingCustomerOrders] = useState(false);
    const [isLoadingDeliveryItems, setIsLoadingDeliveryItems] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || null,
                is_locked: initialData.is_locked || false,
                selected_delivery_notes: initialData.linked_delivery_notes?.length
                    ? initialData.linked_delivery_notes
                    : (initialData.delivery_note?.id
                        ? [initialData.delivery_note]
                        : []),
                invoice_number: initialData.invoice_number || '',
                invoice_date:  initialData.initialType === 'create' ? 
                    new Date().toISOString().split('T')[0] : 
                    initialData.invoice_date || '',
                lpo_number: initialData.lpo_number || '',
                lpo_date: initialData.lpo_date || '',
                customer: initialData.customer || null,
                customer_name: initialData.customer_name || '',
                address1: initialData.address1 || '',
                address2: initialData.address2 || '',
                phone_number: initialData.phone_number || '',
                invoice_description: initialData.invoice_description || {},
                customer_lpo: initialData.customer_lpo || '',

                gross_total: initialData.gross_total || '',
                discount_percentage: initialData.discount_percentage || '',
                discount_amount: initialData.discount_amount || '',
                sub_total: initialData.sub_total || '',
                vat_amount: initialData.vat_amount || '',
                net_total: initialData.net_total || '',
                delivery_notes: initialData.delivery_notes || [],
                items: initialData.items || [],
            });
        }
    }, [initialData]);

    const calculateFormData = (formData) => {
        const grossTotal = calculateGrossTotal(formData.items);

        const overallDiscountAmount = calculateOverallDiscount(
            grossTotal,
            parseFloat(formData.discount_percentage) || 0,
            parseFloat(formData.discount_amount) || 0
        );
        const itemsWithHeaderDiscount = distributeHeaderDiscount(
            formData.items,
            overallDiscountAmount
        );
        const totals = calculateTotals(itemsWithHeaderDiscount);

        return {
            gross_total: grossTotal.toFixed(3),
            discount_amount: overallDiscountAmount.toFixed(3),
            sub_total: (grossTotal - overallDiscountAmount).toFixed(3),
            vat_amount: totals.vatAmount.toFixed(3),
            net_total: totals.netTotal.toFixed(3),
            items: itemsWithHeaderDiscount,
        };
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => {
            let newData = { ...prev };

            // Validate discount_percentage
            if (name === 'discount_percentage') {
                const numValue = parseFloat(value) || 0;
                if (numValue > 100) {
                    return prev; // Don't update if value exceeds 100
                }
            }

            newData[name] = type === 'checkbox' ? checked : value;
            const calculatedData = calculateFormData(newData);

            return {
                ...newData,
                ...calculatedData
            };
        });

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleDiscountChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            return {
                ...prev,
                [name]: value
            }
        });

        if (value.length > 0) {
            clearTimeout(typingTimer);
            const timer = setTimeout(() => doneTyping(value, name), doneTypingInterval);
            setTypingTimer(timer);
        } else {
            doneTyping(value, name);
            clearTimeout(typingTimer);
        }
    }
    const doneTyping = async (value, name) => {
        setFormData(prev => {
            let newData = { ...prev };
            newData[name] = value;
            if(prev.gross_total*1 < value*1) {
                newData[name] = prev.gross_total;
            }

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            }; 
        });
    };


    const handleSelectChange = async (name, value) => {
        let delivery_notes = [];
        if (name === 'lpo_number') {
            if (value) {
                try {
                    setIsLoadingCustomerOrders(true);
                    const response = await deliveryNoteService.getDeliveredNotes(value.id);
                    delivery_notes = response?.data;
                } catch (error) {
                    console.error('Error fetching FG items:', error);
                } finally {
                    setIsLoadingCustomerOrders(false);
                }
            }

            setFormData(prev => ({
                ...prev,
                [name]: value ?? null,
                customer_lpo: value?.id ?? '',
                lpo_date: value?.lpo_date ?? '',
                customer_name: value?.customer_name ?? '',
                address1: value?.address1 ?? '',
                address2: value?.address2 ?? '',
                phone_number: value?.phone_number ?? '',
                delivery_notes: delivery_notes,

                selected_delivery_notes: [],
                items: [],
                gross_total: '',
                discount_percentage: '',
                discount_amount: '',
                sub_total: '',
                vat_amount: '',
                net_total: '',
                advance_payment: '',
                balance: '',
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleToggleDeliveryNote = async (order) => {
        const isSelected = formData.selected_delivery_notes.some((dn) => dn.id === order.id);

        if (isSelected) {
            const mergedItems = mergeItemsForDeliveryNote(formData.items, [], order.id, true);
            const selected_delivery_notes = formData.selected_delivery_notes.filter((dn) => dn.id !== order.id);
            setFormData((prev) => {
                const newData = {
                    ...prev,
                    selected_delivery_notes,
                    items: mergedItems,
                };
                const calculatedData = calculateFormData(newData);
                return { ...newData, ...calculatedData };
            });
            return;
        }

        try {
            setIsLoadingDeliveryItems(true);
            const response = await deliveryNoteService.getOne(order.id);
            const deliveryNote = response?.data;
            const noteItems = deliveryNote?.items || [];
            const noteRef = {
                id: deliveryNote?.id || order.id,
                label: deliveryNote?.voucher_number || order.voucher_number || '',
                voucher_number: deliveryNote?.voucher_number || order.voucher_number || '',
            };

            setFormData((prev) => {
                const mergedItems = mergeItemsForDeliveryNote(prev.items, noteItems, noteRef.id, false);
                const selected_delivery_notes = [...prev.selected_delivery_notes, noteRef];
                const isFirstSelection = prev.selected_delivery_notes.length === 0;

                let newData = {
                    ...prev,
                    selected_delivery_notes,
                    items: mergedItems,
                };

                if (isFirstSelection) {
                    newData = {
                        ...newData,
                        customer: deliveryNote?.customer || null,
                        customer_name: deliveryNote?.customer_name || '',
                        address1: deliveryNote?.address1 || '',
                        address2: deliveryNote?.address2 || '',
                        phone_number: deliveryNote?.phone_number || '',
                    };
                }

                const calculatedData = calculateFormData(newData);
                return { ...newData, ...calculatedData };
            });
        } catch (error) {
            console.error('Error fetching delivery note:', error);
        } finally {
            setIsLoadingDeliveryItems(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            const payload = {
                ...formData,
                delivery_notes: formData.selected_delivery_notes.map(({ id }) => ({ id })),
                delivery_note: formData.selected_delivery_notes[0] || null,
            };
            await onSave(payload);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || formData.is_locked || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);


    const deliveryNoteModal = useModal();
    const deliveryNoteExternalCondition = {
        rules: {
            condition: 'and',
            children: [
                {
                    type: 'rule',
                    query: {
                        operator: 'equal',
                        rule: 'delivery_notes.delivery_type',
                        type: 'text',
                        value: 2
                    }
                },
                {
                    type: 'rule',
                    query: {
                        operator: 'equal',
                        rule: 'delivery_notes.is_invoiced',
                        type: 'text',
                        value: 0
                    }
                }
            ]
        }
    };

    const handleDeliveryNoteSelected = (deliveryNote) => {
        const updatedValue = {
            id: deliveryNote?.lpo_number,
            label: deliveryNote?.lpo_label,
            address1: deliveryNote?.address1,
            address2: deliveryNote?.address2,
            phone_number: deliveryNote?.phone_number,
            customer: deliveryNote?.customer,
            customer_name: deliveryNote?.lpo_customer_name,
        }

        handleSelectChange('lpo_number', updatedValue);
        deliveryNoteModal.hide();
    };

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Sale Invoice</h5>
                </div>
                <div className="d-flex justify-content-end">
                    {formData?.id && (
                        <a href={`${process.env.PUBLIC_URL}/sale_invoices/view/${formData.id}?preview=true`} target="_blank" className="btn btn-primary me-2" >
                            <i className="fas fa-print"></i> Print Preview
                        </a>
                    )}  
                </div>
            </div>
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="invoice_number"
                            label="Invoice Number"
                            value={formData.invoice_number}
                            onChange={handleChange}
                            placeholder="Enter invoice number"
                            disabled={true}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="invoice_date"
                            label="Invoice Date"
                            value={formData.invoice_date}
                            onChange={handleChange}
                            placeholder="Enter invoice date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="delivery_note"
                            label="Delivery Note(s)"
                            value={
                                formData.selected_delivery_notes?.length
                                    ? {
                                        id: formData.selected_delivery_notes.map((dn) => dn.id).join(','),
                                        label: formData.selected_delivery_notes
                                            .map((dn) => dn.label || dn.voucher_number)
                                            .filter(Boolean)
                                            .join(', '),
                                    }
                                    : null
                            }
                            onChange={() => {}}
                            errors={errors}
                            required={false}
                            isSubmitting={true}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">

                        <CommonAsyncSelect
                            name="lpo_number"
                            label="LPO Number"
                            cacheOptions={false}
                            value={formData.lpo_number}
                            onChange={(value) => handleSelectChange('lpo_number', value)}
                            loadOptions={async (inputValue) => {
                                const response = await jobOrderLpoService.searchDeliveredLpos(inputValue);
                                return response.data.map(lpo => ({
                                    id: lpo.id,
                                    label: lpo.label,
                                    lpo_date: lpo.lpo_date,
                                    customer: lpo.customer,
                                    customer_name: lpo.customer_name,
                                    address1: lpo.address1,
                                    address2: lpo.address2,
                                    phone_number: lpo.phone_number
                                }));
                            }}
                            isSubmitting={isSubmitting || formData?.id}
                            errors={errors}
                            required={true}
                            actionButtons={[
                                {
                                    icon: 'fa fa-bars',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => deliveryNoteModal.show()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            name="customer_name"
                            label="Customer Name"
                            rows={1}
                            value={formData.customer_name}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="phone_number"
                            label="Phone Number"
                            value={formData.phone_number}
                            required={false}
                            disabled={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Address 1"
                            name="address1"
                            rows={1}
                            value={formData.address1}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="address2"
                            label="Address 2"
                            value={formData.address2}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            name="invoice_description"
                            label="Invoice Description"
                            rows={1}
                            value={formData.invoice_description}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    
                </div>
        
                {/* Customer LPO field — editable even on locked invoices */}
            <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="customer_lpo"
                            label="Customer LPO"
                            value={formData.customer_lpo || ''}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                        {formData?.id && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm mb-3"
                                disabled={isSavingLpo}
                                onClick={async () => {
                                    if (isSavingLpo) return;
                                    setIsSavingLpo(true);
                                    try {
                                        await dispatch(updateSaleInvoiceCustomerLpo(formData.id, formData.customer_lpo));
                                        toast.success('Customer LPO updated successfully', {
                                            position: 'top-right',
                                            autoClose: 3000,
                                            hideProgressBar: false,
                                        });
                                    } catch (error) {
                                        toast.error(error.message || 'Failed to update Customer LPO', {
                                            position: 'top-right',
                                            autoClose: 3000,
                                            hideProgressBar: false,
                                        });
                                    } finally {
                                        setIsSavingLpo(false);
                                    }
                                }}
                            >
                                {isSavingLpo ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i> Save LPO
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
                {
                    (!formData.id) && (
                        <div className="row">
                            <div className="col-md-12">
                                <h5 className="mb-3">Delivery Notes</h5>
                                <div className="table-responsive mb-2">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Voucher Number</th>
                                                <th>Delivery Date</th>
                                                <th>Customer Name</th>
                                                <th>LPO Number</th>
                                                <th>Phone Number</th>
                                                <th>Net Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.delivery_notes && formData.delivery_notes.length > 0 ? (
                                                formData.delivery_notes.map(order => (
                                                    <tr key={order.id}>
                                                        <td>
                                                            <input type="checkbox" 
                                                                checked={formData.selected_delivery_notes.some((dn) => dn.id === order.id)}
                                                                onChange={() => handleToggleDeliveryNote(order)}
                                                                disabled={isSubmitting || isLoadingDeliveryItems}
                                                            />
                                                        </td>
                                                        <td className="text-nowrap">{order.voucher_number}</td>
                                                        <td className="text-nowrap">{order.voucher_date}</td>
                                                        <td className="text-nowrap">{order.customer_name}</td>
                                                        <td className="text-nowrap">{order.lpo_number}</td>
                                                        <td className="text-nowrap">{order.phone_number}</td>
                                                        <td className="text-nowrap">{order.net_total}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center">No orders available</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )
                }
                <div className="row">
                    <div className="col-md-12">
                        <h5 className="mb-3">Invoice Items</h5>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="table-responsive mb-2">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Item Code</th>
                                        <th>Item Name</th>
                                        <th>Unit</th>
                                        <th>Qty</th>
                                        <th>Unit Price</th>
                                        <th>Discount %</th>
                                        <th>Line Amt.</th>
                                        <th>Header Discount</th>
                                        <th>Amount</th>
                                        <th>VAT Amt.</th>
                                        <th>Amount Incl. VAT</th>
                                        <th>VAT Prod. Group</th>
                                        <th>VAT Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.items && formData.items.length > 0 ? (
                                        formData.items.map((item, index) => (
                                            <tr key={item.id ?? index}>
                                                <td className="text-nowrap">{item?.item?.item_code}</td>
                                                <td className="text-nowrap">{item?.item_description || '-'}</td>
                                                <td className="text-nowrap">{item.unit}</td>
                                                <td className="text-nowrap">{item.quantity}</td>
                                                <td className="text-nowrap">{(Number(item?.unit_price) || 0).toFixed(3)}</td>
                                                <td className="text-nowrap">{item.discount_percentage}</td>
                                                <td className="text-nowrap">{(Number(item?.line_amount) || 0).toFixed(3)}</td>
                                                <td className="text-nowrap">{(Number(item?.header_discount) || 0).toFixed(3)}</td>
                                                <td className="text-nowrap">{(Number(item?.amount_excl_vat) || 0).toFixed(3)}</td>
                                                <td className="text-nowrap">{(Number(item?.vat_amount) || 0).toFixed(3)}</td>
                                                <td className="text-nowrap">{(Number(item?.amount_incl_vat) || 0).toFixed(3)}</td>
                                                <td className="text-nowrap">{item?.vat_product_group?.label}</td>
                                                <td className="text-nowrap">{item.vat_percentage}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="12" className="text-center">No items available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="row mt-4 d-flex justify-content-end">

                    <div className="col-12 col-md-4 order-md-2">
                        <div className="row mb-2">
                            <label className="col-md-6 col-form-label">Gross Total:</label>
                            <div className="col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="gross_total"
                                    value={formData.gross_total}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <label className="col-md-6 col-form-label">Discount %:</label>
                            <div className="col-md-6">
                                <CommonInput
                                    type="number"
                                    className="mb-0 custom-style text-end"
                                    name="discount_percentage"
                                    value={formData.discount_percentage}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                    errors={errors}
                                    min={0}
                                    max={100}
                                />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <label className="col-md-6 col-form-label">Discount Amt.:</label>
                            <div className="col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="discount_amount"
                                    value={formData.discount_amount}
                                    onChange={handleDiscountChange}
                                    disabled={isSubmitting}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <label className="col-md-6 col-form-label">Sub Total:</label>
                            <div className="col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="sub_total"
                                    value={formData.sub_total}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <label className="col-md-6 col-form-label">VAT Amount:</label>
                            <div className="col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="vat_amount"
                                    value={formData.vat_amount}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <label className="col-md-6 col-form-label">Net Total:</label>
                            <div className="col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="net_total"
                                    value={formData.net_total}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <DataSelectionModal
                    show={deliveryNoteModal.isVisible}
                    onHide={deliveryNoteModal.hide}
                    title=""
                    DataComponent={Deliverynotes}
                    selectionMode="single"
                    onDataSelected={handleDeliveryNoteSelected}
                    componentProps={{
                        // Any props specific to Suppliers component
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: deliveryNoteExternalCondition
                    }}
                />
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : formData.is_locked ? (
                        <>
                            <i className="fa fa-lock me-2" /> Locked
                        </>
                    ) : isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default SaleInvoiceForm; 